import { useEffect, useId, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  api,
  ApiError,
  type ConnectorSummary,
  type FieldDef,
  type UserStructuredProps,
} from "../../lib/api";

import { FileDropzone } from "../FileDropzone";
import { ScheduledFileInput, SyncFrequencySection, sanitizeFilePath } from "./ConfigureConnectorModal";
import { useToast } from "../toast/ToastProvider";
import { cn } from "../../lib/cn";
import { litmusCronFromConfig, describeSchedule } from "../AddSourceDialog";
import {
  isStructuredPropertiesValid,
  StructuredPropertiesEditor,
} from "../StructuredPropertiesEditor";
import { SourceForm, getMissingRequiredFields, type SourceFormValues } from "../SourceForm";

type ImportMode = "one-time" | "scheduled";
type SyncFrequency = "monthly" | "daily" | "weekly";

export interface SourceFull {
  id: string;
  name: string;
  connectorId: string;
  config: Record<string, unknown>;
  structuredProperties?: UserStructuredProps;
  scheduleCron: string;
  enabled: boolean;
  includeRawAttachments: boolean;
}

interface EditConnectorModalProps {
  source: SourceFull;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditConnectorModal({ source, open, onOpenChange }: EditConnectorModalProps) {
  const qc = useQueryClient();
  const { show } = useToast();
  const uid = useId();
  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.get<ConnectorSummary[]>("/api/v1/connectors"),
    enabled: open,
  });
  const connector = connectors.data?.find((c) => c.id === source.connectorId);

  const isScheduledSource = source.config["sync_mode"] === "scheduled";
  const initialFilePath = (source.config["file_path"] as string) ?? "";

  const [mode, setMode] = useState<ImportMode>(isScheduledSource ? "scheduled" : "one-time");
  const [name, setName] = useState(source.name);
  const [formValues, setFormValues] = useState<SourceFormValues>(() => ({
    config: buildFormConfig(source.config, connector),
    secrets: {},
  }));
  const [uploadedFileId, setUploadedFileId] = useState(isScheduledSource ? "" : initialFilePath);
  const [scheduledFilePath, setScheduledFilePath] = useState(
    isScheduledSource ? initialFilePath : "",
  );
  const [structuredProperties, setStructuredProperties] = useState<UserStructuredProps>(
    source.structuredProperties ?? {},
  );

  const [frequency, setFrequency] = useState<SyncFrequency>(
    (source.config["sync_frequency"] as SyncFrequency) ?? "monthly",
  );
  const [syncDay, setSyncDay] = useState((source.config["sync_day"] as string) ?? "Monday");
  const [syncDate, setSyncDate] = useState((source.config["sync_date"] as string) ?? "1");
  const [syncTime, setSyncTime] = useState((source.config["sync_time"] as string) ?? "02:00");
  const [syncTimezone, setSyncTimezone] = useState(
    (source.config["sync_timezone"] as string) ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [error, setError] = useState<string | null>(null);

  // Re-sync all state when the dialog opens for a different source.
  useEffect(() => {
    if (!open) return;
    const sched = source.config["sync_mode"] === "scheduled";
    setMode(sched ? "scheduled" : "one-time");
    setName(source.name);
    setFormValues({ config: buildFormConfig(source.config, connector), secrets: {} });
    const fp = (source.config["file_path"] as string) ?? "";
    setUploadedFileId(sched ? "" : fp);
    setScheduledFilePath(sched ? fp : "");
    setStructuredProperties(source.structuredProperties ?? {});
    setFrequency((source.config["sync_frequency"] as SyncFrequency) ?? "monthly");
    setSyncDay((source.config["sync_day"] as string) ?? "Monday");
    setSyncDate((source.config["sync_date"] as string) ?? "1");
    setSyncTime((source.config["sync_time"] as string) ?? "02:00");
    setSyncTimezone(
      (source.config["sync_timezone"] as string) ??
        Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    setError(null);
  }, [open, source.id]);

  const isScheduled = mode === "scheduled";

  const fileFields = connector?.schema.fields.filter((f) => f.type === "file") ?? [];
  const hasFileFields = fileFields.length > 0;
  const fileExtensions = fileFields.flatMap((f) => f.accept ?? []);

  const schemaWithoutFiles = connector
    ? { ...connector.schema, fields: connector.schema.fields.filter((f) => f.type !== "file") }
    : undefined;

  const scheduleCron = isScheduled
    ? litmusCronFromConfig(frequency, syncTime, syncTimezone, syncDay, syncDate)
    : "";
  const scheduleDesc = isScheduled
    ? describeSchedule(frequency, syncTime, syncTimezone, syncDay, syncDate)
    : "";

  const canSave =
    name.trim() !== "" &&
    (!hasFileFields || (isScheduled ? scheduledFilePath.trim() !== "" : uploadedFileId !== ""));

  function handleClose() {
    if (!isScheduled && uploadedFileId && uploadedFileId !== initialFilePath) {
      fetch(`/api/v1/uploads/${uploadedFileId}`, {
        method: "DELETE",
        credentials: "same-origin",
      }).catch(() => {});
    }
    onOpenChange(false);
  }

  const save = useMutation({
    mutationFn: (body: unknown) =>
      api.patch(`/api/v1/sources/${encodeURIComponent(source.id)}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources"] });
      qc.invalidateQueries({ queryKey: ["sources", source.id] });
      show("Source saved", `"${name.trim()}" updated`, "success");
      onOpenChange(false);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Save failed";
      setError(msg);
      show("Save failed", msg, "error");
    },
  });

  function submit() {
    if (!connector) return;
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (hasFileFields) {
      if (isScheduled && !sanitizeFilePath(scheduledFilePath)) {
        setError("Enter the absolute path to the file on the agent host.");
        return;
      }
      if (!isScheduled && !uploadedFileId) {
        setError("Please select a file.");
        return;
      }
    }
    if (schemaWithoutFiles) {
      const missing = getMissingRequiredFields(schemaWithoutFiles, formValues, true);
      if (missing.length > 0) {
        setError(`Required: ${missing.join(", ")}`);
        return;
      }
    }
    if (!isStructuredPropertiesValid(structuredProperties)) {
      setError("Fix the invalid or conflicting static metadata fields before continuing.");
      return;
    }
    setError(null);

    save.mutate({
      name: name.trim(),
      config: {
        ...formValues.config,
        ...(hasFileFields
          ? { file_path: isScheduled ? sanitizeFilePath(scheduledFilePath) : uploadedFileId }
          : {}),
        ...(isScheduled
          ? {
              sync_mode: "scheduled",
              sync_frequency: frequency,
              sync_time: syncTime,
              sync_timezone: syncTimezone,
              sync_day: syncDay,
              sync_date: syncDate,
            }
          : { sync_mode: undefined }),
      },
      secrets: formValues.secrets,
      structuredProperties,
      scheduleCron: scheduleCron.trim(),
    });
  }

  const fileFieldDef: FieldDef = {
    key: "file_path",
    label: isScheduled ? "Project / Backup File" : "Source Export File",
    type: "file",
    required: true,
    accept: fileExtensions.length > 0 ? fileExtensions : undefined,
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[1400] bg-ink/40 backdrop-blur-sm" />
        <Dialog.Content
          aria-describedby={`${uid}-desc`}
          className="
            fixed left-1/2 top-[72px] z-[1401]
            w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2
            rounded-soft border border-rule bg-surface shadow-cardLift
            max-h-[calc(100vh-96px)] overflow-y-auto no-scrollbar
          "
        >
          <div className="flex items-start justify-between gap-4 px-7 pt-6 pb-0">
            <div>
              <Dialog.Title asChild>
                <h2 className="font-display text-[20px] font-semibold tracking-tight text-ink">
                  Edit {connector?.displayName ?? source.connectorId}
                </h2>
              </Dialog.Title>
              <p id={`${uid}-desc`} className="mt-1 text-[12px] text-ash">
                Connector type cannot be changed after creation.
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Close"
                className="mt-0.5 shrink-0 rounded p-1 text-ash hover:bg-paper hover:text-ink"
              >
                <CloseIcon />
              </button>
            </Dialog.Close>
          </div>

          <div className="space-y-5 px-7 pb-7 pt-5">
            {!connector ? (
              connectors.isLoading ? (
                <p className="text-sm text-ash">Loading connector schema…</p>
              ) : (
                <p className="text-sm text-rose-700">
                  Connector "{source.connectorId}" is no longer registered on this agent build.
                </p>
              )
            ) : (
              <>
                <label className="block">
                  <span className="mb-1.5 block font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash">
                    Name
                  </span>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input"
                    aria-required="true"
                  />
                </label>

                <div>
                    <div
                      className="flex w-full overflow-hidden rounded border border-rule bg-paper p-1 gap-1"
                      role="group"
                      aria-label="Import mode"
                    >
                      {(
                        [
                          {
                            value: "one-time" as ImportMode,
                            label: "One-time import",
                            subtitle: "Ingest once now",
                          },
                          {
                            value: "scheduled" as ImportMode,
                            label: "Scheduled sync",
                            subtitle: "Keep it fresh",
                          },
                        ] as const
                      ).map((opt) => {
                        const active = mode === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setMode(opt.value)}
                            aria-pressed={active}
                            className={cn(
                              "flex-1 rounded py-2.5 px-4 text-center transition-all select-none",
                              active
                                ? "bg-white text-ink shadow-sm cursor-default"
                                : "bg-transparent text-ash cursor-pointer hover:text-ink",
                            )}
                          >
                            <div
                              className={cn(
                                "text-[13px] leading-tight",
                                active ? "font-semibold" : "font-medium",
                              )}
                            >
                              {opt.label}
                            </div>
                            <div className="mt-0.5 text-[11px] leading-tight text-ash">
                              {opt.subtitle}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2.5 text-[12.5px] leading-relaxed text-ash">
                      {isScheduled
                        ? "The agent re-reads the selected project/backup file (or .zip) on a schedule and re-ingests changed metadata - no manual re-uploads."
                        : "Upload a vendor export and ingest its metadata once. Re-run anytime with a single refresh."}
                    </p>
                </div>

                {hasFileFields && !isScheduled && (
                  <div>
                    <div className="mb-2">
                      <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-ash">
                        Source Export File <span className="text-rose-600">*</span>
                      </span>
                    </div>
                    <FileDropzone
                      def={fileFieldDef}
                      value={uploadedFileId}
                      onChange={setUploadedFileId}
                      connectorId={connector.id}
                    />
                  </div>
                )}

                {hasFileFields && isScheduled && (
                  <ScheduledFileInput
                    value={scheduledFilePath}
                    onChange={setScheduledFilePath}
                    accept={fileExtensions.length > 0 ? fileExtensions : undefined}
                  />
                )}

                {schemaWithoutFiles && (
                  <SourceForm
                    schema={schemaWithoutFiles}
                    value={formValues}
                    onChange={setFormValues}
                    connectorId={connector.id}
                    editing
                  />
                )}

                <StructuredPropertiesEditor
                  value={structuredProperties}
                  onChange={setStructuredProperties}
                />

                {isScheduled && (
                  <SyncFrequencySection
                    uid={uid}
                    frequency={frequency}
                    syncDay={syncDay}
                    syncDate={syncDate}
                    syncTime={syncTime}
                    syncTimezone={syncTimezone}
                    scheduleDesc={scheduleDesc}
                    onFrequencyChange={setFrequency}
                    onDayChange={setSyncDay}
                    onDateChange={setSyncDate}
                    onTimeChange={setSyncTime}
                    onTimezoneChange={setSyncTimezone}
                  />
                )}

                {error && (
                  <div
                    role="alert"
                    className="rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700"
                  >
                    {error}
                  </div>
                )}

                <div className="flex justify-end gap-2 border-t border-rule pt-5">
                  <button type="button" onClick={handleClose} className="btn-secondary">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={!canSave || save.isPending}
                    className="btn-primary"
                  >
                    {save.isPending ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function buildFormConfig(
  config: Record<string, unknown>,
  connector: ConnectorSummary | undefined,
): Record<string, unknown> {
  if (!connector) return { ...config };
  const scheduleKeys = new Set([
    "sync_mode",
    "sync_frequency",
    "sync_time",
    "sync_timezone",
    "sync_day",
    "sync_date",
    "file_path",
  ]);
  const out: Record<string, unknown> = {};
  for (const f of connector.schema.fields) {
    if (f.type === "file") continue;
    if (f.key in config) out[f.key] = config[f.key];
  }
  for (const [k, v] of Object.entries(config)) {
    if (!scheduleKeys.has(k) && !(k in out)) out[k] = v;
  }
  return out;
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden>
      <path
        d="M3 3l10 10M13 3L3 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
